// src/components/NotificationBell.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import { ADMIN_UID, formatTime } from "../config";
import { collection, query, where, orderBy, onSnapshot, limit } from "firebase/firestore";

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => {
    try { return JSON.parse(window.sessionStorage?.getItem?.("sf_seen") || "[]"); } catch { return []; }
  });
  const panelRef = useRef(null);
  const isAdmin = user?.uid === ADMIN_UID;

  useEffect(() => {
    if (!user) return;
    // Listen to recent orders for status changes
    const constraints = isAdmin
      ? [orderBy("createdAt", "desc"), limit(50)]
      : [where("userId", "==", user.uid), orderBy("createdAt", "desc"), limit(20)];
    const q = query(collection(db, "orders"), ...constraints);
    const unsub = onSnapshot(q, (snap) => {
      const notes = [];
      snap.docChanges().forEach((change) => {
        if (change.type === "modified") {
          const d = change.doc.data();
          const id = change.doc.id;
          if (d.status === "quoted" && !isAdmin) {
            notes.push({ id: id + "_quoted", orderId: id, type: "quote", message: `Quote received: $${d.quotedPrice}`, time: d.quotedAt });
          }
          if (d.status === "completed" && !isAdmin) {
            notes.push({ id: id + "_completed", orderId: id, type: "label", message: "Your label is ready!", time: d.completedAt });
          }
          if (d.status === "confirmed" && isAdmin) {
            notes.push({ id: id + "_confirmed", orderId: id, type: "confirmed", message: `${d.userName} confirmed order`, time: d.confirmedAt });
          }
          if (d.status === "void_requested" && isAdmin) {
            notes.push({ id: id + "_void", orderId: id, type: "void", message: `${d.userName} requested void`, time: d.voidRequestedAt });
          }
        }
      });
      if (notes.length > 0) {
        setNotifications((prev) => [...notes, ...prev].slice(0, 30));
      }
    });
    return unsub;
  }, [user, isAdmin]);

  // Also listen for new messages
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "messages"),
      where("senderId", "!=", user.uid),
      orderBy("senderId"),
      orderBy("createdAt", "desc"),
      limit(10)
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgNotes = [];
      snap.docChanges().forEach((change) => {
        if (change.type === "added") {
          const d = change.doc.data();
          msgNotes.push({
            id: change.doc.id,
            orderId: d.orderId,
            type: "message",
            message: `${d.senderName}: ${d.text.slice(0, 50)}${d.text.length > 50 ? "..." : ""}`,
            time: d.createdAt,
          });
        }
      });
      if (msgNotes.length > 0) {
        setNotifications((prev) => [...msgNotes, ...prev].slice(0, 30));
      }
    });
    return unsub;
  }, [user]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const unseen = notifications.filter((n) => !seen.includes(n.id));

  const markAllSeen = () => {
    const ids = notifications.map((n) => n.id);
    setSeen(ids);
    try { window.sessionStorage?.setItem?.("sf_seen", JSON.stringify(ids)); } catch {}
  };

  return (
    <div ref={panelRef} style={{ position: "relative" }}>
      <button
        className="btn btn-ghost btn-sm notification-bell"
        onClick={() => { setOpen(!open); if (!open) markAllSeen(); }}
        style={{ position: "relative", padding: "8px 10px" }}
      >
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        {unseen.length > 0 && (
          <span className="notification-count">{unseen.length > 9 ? "9+" : unseen.length}</span>
        )}
      </button>

      {open && (
        <div className="notification-panel animate-slide-down">
          <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#e2e8f0" }}>Notifications</span>
            {notifications.length > 0 && (
              <button onClick={markAllSeen} style={{ background: "none", border: "none", color: "#d4a053", fontSize: 12, cursor: "pointer" }}>
                Mark all read
              </button>
            )}
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                No notifications yet
              </div>
            ) : (
              notifications.slice(0, 15).map((n) => (
                <div key={n.id} className={`notification-item ${!seen.includes(n.id) ? "notification-unread" : ""}`}>
                  <span className={`notification-icon notification-icon-${n.type}`}>
                    {n.type === "quote" ? "$" : n.type === "label" ? "📄" : n.type === "message" ? "💬" : n.type === "void" ? "⊘" : "✓"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.4 }}>{n.message}</p>
                    <p style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{formatTime(n.time)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
